import { useState, useEffect, useRef } from "react";
import AlterMenu from "./AlterMenu";
import extractDomain from "../utils/web/extractDomain";
import addProtocolToLink from "../utils/web/addProtocolToLink";
import redirect from "../utils/routing/routing";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import EditBookmarkModal from "../words/EditBookmarkModal";
import ExplanationModal from "./ExplanationModal";


export default function TranslatableWord({
  interactiveText,
  word,
  wordUpdated,
  translating,
  pronouncing,
  disableTranslation,
  highlightedMWEGroupId,
  setHighlightedMWEGroupId,
  loadingMWEGroupId,
  setLoadingMWEGroupId,
  mweGroupColorMap,
  mweGroupsWithTranslations,
  // Exercise solution highlighting props
  solutionWordIds,
  highlightSolutionExpression,
  setHighlightSolutionExpression,
}) {
  const [showingAlterMenu, setShowingAlterMenu] = useState(false);
  // Null until Explain is tapped. The selection and sentence are kept alongside
  // the text so the modal can show what was actually asked -- and so they
  // survive the menu closing underneath it.
  const [explanation, setExplanation] = useState(null);
  // Explain takes a couple of seconds. Closing the modal while it is still
  // working has to actually close it: without this token the late response
  // calls setExplanation and the modal springs back open by itself.
  const explainRequestRef = useRef(0);
  const refToTranslation = useRef(null);
  const [isClickedToPronounce, setIsClickedToPronounce] = useState(false);
  const [isWordTranslating, setIsWordTranslating] = useState(false);
  const [prevWord, setPreviousWord] = useState("");
  const [isTranslationVisible, setIsTranslationVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Bookmark-shaped snapshot handed to EditBookmarkModal; non-null while the
  // edit dialog is open.
  const [bookmarkBeingEdited, setBookmarkBeingEdited] = useState(null);

  useEffect(() => {
    if (word.isTranslationVisible) {
      setIsTranslationVisible(true);
      return;
    }
    if (word.translation) setIsTranslationVisible(false);
  }, [word]);

  function clickOnWord(e, word) {
    if (word.token.is_like_num || (word.token.is_punct && word.word.length === 1)) return;

    // Compute MWE expression BEFORE any fusion happens (for pronunciation)
    // Must do this early because translate() will detach partner words
    let mweTextToSpeak = null;
    if (word.token?.mwe_group_id && word.findMWEPartners) {
      const partners = word.findMWEPartners();
      if (partners.length > 1) {
        mweTextToSpeak = partners.reduce((acc, p, i) => {
          if (i === 0) return p.word;
          if (partners[i - 1].word.endsWith("-")) return acc + p.word;
          return acc + " " + p.word;
        }, "");
      }
    }

    if (word.translation) {
      if (pronouncing) interactiveText.pronounce(word, null, mweTextToSpeak);
      if ((translating && !isTranslationVisible) || (!translating && isTranslationVisible))
        setIsTranslationVisible(!isTranslationVisible);
      return;
    }
    if (translating) {
      if (!disableTranslation) {
        setIsLoading(true);
        setPreviousWord(word.word);
        setIsWordTranslating(true);
        // Set MWE group loading state so all partner words pulse together
        const mweGroupId = word.token?.mwe_group_id;
        if (mweGroupId && setLoadingMWEGroupId) {
          setLoadingMWEGroupId(mweGroupId);
        }
        // For MWE words, update prevWord to fused text and trigger re-render
        // This ensures the loading animation shows "suntem prezenți" not just "suntem"
        const onFusionComplete = mweGroupId ? () => {
          setPreviousWord(word.word); // word.word is now the fused MWE text
          wordUpdated();
        } : null;
        interactiveText.translate(word, true, () => {
          wordUpdated();
          setIsLoading(false);
          setIsWordTranslating(false);
          setIsTranslationVisible(true);
          // Clear MWE loading state
          if (mweGroupId && setLoadingMWEGroupId) {
            setLoadingMWEGroupId(null);
          }
          // Backend flagged a 3-way provider disagreement; surface the
          // alternatives menu so the user can pick the right answer.
          if (word.disagreement) {
            openAlterMenu(word);
          }
        }, onFusionComplete);
      } else {
        // For non-translatable words in exercises, track the click
        if (interactiveText.trackWordClick) {
          interactiveText.trackWordClick(word);
        }
      }
    }
    if (pronouncing || isClickedToPronounce) {
      setIsClickedToPronounce(true);
      interactiveText.pronounce(word, null, mweTextToSpeak);
    }
  }

  function openAlterMenu(word) {
    // ADR 022: the menu renders directly from word.alternatives (populated
    // eagerly by /translate_word). Opening the menu is normally a pure
    // render — no network call, no row-shifting under the user's finger.
    setShowingAlterMenu(true);

    // Exception: if /translate_word came back without alternatives — which
    // happens on the own-past-translation early-return path where the
    // backend hands the user their prior answer instantly and skips the
    // voter — lazy-fetch them now that the user has shown interest by
    // opening the menu. Single request, single update, no streaming.
    //
    // No need to gate on word.mweExpression: the backend voter accepts
    // multi-word input too (zeeguu/api#628). word._lazyAlternativesFetched
    // guards against re-firing if the user closes and reopens the menu.
    if (!word.alternatives?.length && !word._lazyAlternativesFetched) {
      word._lazyAlternativesFetched = true;
      interactiveText.fetchAlternatives(
        word,
        () => wordUpdated(word),
        () => wordUpdated(word),
      );
    }
  }

  // Read-only text (the account-less public reader): translations can be
  // shown and hidden, but there's no bookmark behind them to edit or delete.
  const readOnly = !!interactiveText.readOnly;

  function toggleAlterMenu(e, word) {
    if (readOnly) return;
    if (showingAlterMenu) {
      setShowingAlterMenu(false);
      return;
    }
    openAlterMenu(word);
  }

  function unlinkLastWord(e, word) {
    setIsLoading(true);
    interactiveText.api.deleteBookmark(
      word.bookmark_id,
      (response) => {
        if (response === "OK") {
          // delete was successful; log and close
          let withoutLastWord = word.unlinkLastWord();
          interactiveText.translate(withoutLastWord, true, () => {
            withoutLastWord.isTranslationVisible = true;
            let unlinkedWord = withoutLastWord.next;
            interactiveText.translate(unlinkedWord, false, () => {
              unlinkedWord.isTranslationVisible = true;
              wordUpdated();
              setIsLoading(false);
            });
          });
        }
      },
      (error) => {
        // onError
        console.error(error);
      },
    );
  }

  function deleteTranslation(e, word) {
    interactiveText.api.deleteBookmark(
      word.bookmark_id,
      (response) => {
        if (response === "OK") {
          // delete was successful; log and close
          word.splitIntoComponents();
          wordUpdated();
        }
      },
      (error) => {
        // onError
        console.log(error);
        alert("something went wrong and we could not delete the bookmark; try again later.");
      },
    );
  }

  function ungroupMwe(e, word) {
    // Get article_id from context identifier
    const articleId = interactiveText.contextIdentifier?.article_id;
    const mweExpression = word.mweExpression;
    const sentenceText = word.getSentenceText?.() || "";
    const bookmarkId = word.bookmark_id;

    if (!articleId || !mweExpression || !sentenceText || !bookmarkId) {
      console.error("Missing data for MWE ungroup:", { articleId, mweExpression, sentenceText, bookmarkId });
      return;
    }

    interactiveText.api.disableMweGrouping(
      articleId,
      mweExpression,
      sentenceText,
      bookmarkId,
      () => {
        // Success - split the word back into components and clear MWE metadata
        word.splitAndClearMWE();
        setShowingAlterMenu(false);
        wordUpdated();
      },
      (error) => {
        console.error("Failed to ungroup MWE:", error);
        alert("Could not ungroup words. Please try again.");
      },
    );
  }

  function selectAlternative(alternative, preferredSource) {
    interactiveText.selectAlternative(word, alternative, preferredSource, () => {
      wordUpdated();
      setShowingAlterMenu(false);
    });
  }

  // The alter menu is viewport-fixed and self-closes on scroll, so it can't
  // host a text field (the soft keyboard's reveal-scroll tears it down). Hand
  // the word/translation pair to the same modal the words list and the
  // session history use instead.
  function openTranslationEditor() {
    const [context] = interactiveText.getContextAndCoordinates(word);
    setShowingAlterMenu(false);
    setBookmarkBeingEdited({
      id: word.bookmark_id,
      from: word.mweExpression || word.word,
      to: word.translation,
      context: context,
    });
  }

  // Only the translation is editable here, so this reuses the same save path
  // as picking an alternative from the menu: a translation-only bookmark
  // update that leaves the word's token coordinates in the article intact.
  function saveEditedTranslation(bookmark, newWord, newTranslation) {
    interactiveText.selectAlternative(word, newTranslation, "User Suggested", () => {
      wordUpdated();
      setBookmarkBeingEdited(null);
    });
  }

  function hideAlterMenu() {
    setShowingAlterMenu(false);
  }

  // Check if this word has MWE partners in the same sentence
  function hasMWEPartnersInSameSentence() {
    if (!word.token.mwe_group_id) return false;
    return word.findMWEPartners().length > 1;
  }

  // Check if this word is part of the solution expression (for exercises)
  function isPartOfSolution() {
    return solutionWordIds && solutionWordIds.includes(word.id);
  }

  function handleMouseEnter() {
    // Solution expression highlighting for exercises (multi-word bookmarks)
    if (isPartOfSolution() && setHighlightSolutionExpression) {
      setHighlightSolutionExpression(true);
      return; // Don't also do MWE highlighting
    }
    // Regular MWE highlighting for reader
    const groupId = word.token?.mwe_group_id;
    if (hasMWEPartnersInSameSentence() && setHighlightedMWEGroupId) {
      setHighlightedMWEGroupId(groupId);
    }
  }

  function handleMouseLeave() {
    // Solution expression highlighting for exercises
    if (isPartOfSolution() && setHighlightSolutionExpression) {
      setHighlightSolutionExpression(false);
      return;
    }
    // Regular MWE highlighting for reader
    if (hasMWEPartnersInSameSentence() && setHighlightedMWEGroupId) {
      setHighlightedMWEGroupId(null);
    }
  }

  // Check if this word is part of a translated MWE
  function isTranslatedMWE() {
    if (word.token.mwe_group_id && mweGroupsWithTranslations?.has(word.token.mwe_group_id)) return true;
    if (word.mweExpression && word.translation) return true;
    if (word.isMwePartner && word.mweExpression) return true;
    return false;
  }

  // Get color class for separated MWEs (mwe-color-0 through mwe-color-4)
  function getMWEColorClass() {
    if (!word.token.mwe_is_separated) return "";
    if (word.token.mwe_group_id && mweGroupColorMap) {
      const colorIndex = mweGroupColorMap[word.token.mwe_group_id];
      if (colorIndex !== undefined) return `mwe-color-${colorIndex}`;
    }
    return word.mweExpression ? "mwe-color-0" : "";
  }

  // Get CSS classes for special tokens (punctuation, numbers, symbols)
  function getSpecialTokenClasses(word) {
    const classes = [];
    if (word.token.is_like_num) classes.push("number");

    if (word.token.has_space !== undefined) {
      // Stanza tokenizer
      if (word.token.is_punct || word.token.is_like_symbol) classes.push("no-hover");
    } else {
      // NLTK tokenizer
      if (word.token.is_punct) {
        classes.push("punct", "no-hover");
      }
      if (word.token.is_left_punct ||
          (word.token.is_punct && word.prev && [":", ".", ","].includes(word.prev.word.trim()))) {
        classes.push("left-punct");
      }
      if (["–", "—", "\u201c", "\u2019", '"'].includes(word.word.trim())) {
        classes.push("no-margin");
      }
    }
    return classes;
  }

  // Get MWE-related CSS classes
  function getMWEClasses(word) {
    const classes = [];
    const groupId = word.token?.mwe_group_id;
    if (!groupId) return classes;

    const translated = isTranslatedMWE();
    const colorClass = getMWEColorClass();
    const isHighlighted = highlightedMWEGroupId === groupId && hasMWEPartnersInSameSentence();
    const isLoading = loadingMWEGroupId === groupId;

    if (translated) {
      classes.push(colorClass || "mwe-adjacent");
    } else {
      classes.push("mwe-hover-hint", colorClass);
    }

    if (isLoading) classes.push("mwe-loading", colorClass);
    if (isHighlighted) {
      classes.push("mwe-hover-active");
      if (!translated) classes.push(colorClass);
    }

    return classes;
  }

  // Get CSS classes for solution expression highlighting in exercises
  function getSolutionHighlightClasses() {
    if (highlightSolutionExpression && isPartOfSolution()) {
      return ["solution-hover-active"];
    }
    return [];
  }

  function getWordClass(word) {
    return [...getSpecialTokenClasses(word), ...getMWEClasses(word), ...getSolutionHighlightClasses()].filter(Boolean).join(" ");
  }

  const wordClass = getWordClass(word);

  // Don't render words that have been fused into an MWE (marked for skip)
  // This prevents duplication during loading animation when fuseMWEPartners
  // has already run but the component is still mounted
  if (word.token?.skipRender) {
    return null;
  }

  if (word.token.is_like_email)
    return (
      <>
        <z-tag>
          <a href={"mailto:" + word.word}>{extractDomain(word.word) + " "}</a>
        </z-tag>
      </>
    );
  if (word.token.is_like_url)
    return (
      <>
        <z-tag onClick={() => redirect(addProtocolToLink(word.word), true)}>
          <span className="link-style">{extractDomain(word.word) + " "}</span>
        </z-tag>
      </>
    );

  // Render simple (non-translated) word: no translation yet, or translation disabled (e.g., in exercises)
  if ((!isWordTranslating && !word.translation && !isClickedToPronounce) || disableTranslation) {
    return (
      <>
        <z-tag
          class={wordClass}
          onClick={(e) => clickOnWord(e, word)}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {word.word + (word.token.has_space === true && !word.word.endsWith("-") && !word.next?.word?.startsWith("-") ? " " : "")}
        </z-tag>
      </>
    );
  }

  return (
    <>
      <z-tag class={wordClass} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {word.translation && (isTranslationVisible || word.isTranslationVisible) && (
          <z-tran chosen={word.translation} translation0={word.translation} ref={refToTranslation}>
            <span className="translationContainer">
              <span className="hide low-oppacity translation-icon">
                <VisibilityOffIcon
                  fontSize="8px"
                  onClick={(e) => {
                    // Toggle both React state and word object flag
                    const newVisibility = !(isTranslationVisible || word.isTranslationVisible);
                    setIsTranslationVisible(newVisibility);
                    word.isTranslationVisible = newVisibility;
                    setShowingAlterMenu(false);
                  }}
                />
              </span>
              <span className="translation" onClick={(e) => toggleAlterMenu(e, word)}>
                {word.translation}
              </span>
              {!readOnly && (
                <span className="arrow" onClick={(e) => toggleAlterMenu(e, word)}>
                  {showingAlterMenu ? "▲" : "▼"}
                </span>
              )}
              {!readOnly && word.mergedTokens.length > 1 && !word.mweExpression && (
                <span className="unlink low-oppacity translation-icon">
                  <LinkOffIcon
                    fontSize="8px"
                    onClick={(e) => {
                      unlinkLastWord(e, word);
                    }}
                  />
                </span>
              )}
            </span>
          </z-tran>
        )}
        <z-orig>
          {isWordTranslating ? (
            <span className={isLoading ? " loading" : ""}> {prevWord} </span>
          ) : (
            <span className={isLoading ? " loading" : ""} onClick={(e) => clickOnWord(e, word)}>
              {word.word}{" "}
            </span>
          )}
          {showingAlterMenu && (
            <AlterMenu
              word={word}
              setShowingAlternatives={setShowingAlterMenu}
              selectAlternative={selectAlternative}
              editTranslation={openTranslationEditor}
              hideAlterMenu={hideAlterMenu}
              deleteTranslation={deleteTranslation}
              ungroupMwe={ungroupMwe}
              explainSelection={(w) => {
                const selection = w.mweExpression || w.word;
                const token = ++explainRequestRef.current;
                const isStale = () => explainRequestRef.current !== token;
                setExplanation({ selection, context: null, text: null, isLoading: true, error: false });
                hideAlterMenu();
                interactiveText.explainSelection(
                  w,
                  ({ selection, context, explanation }) => {
                    if (isStale()) return;
                    setExplanation({ selection, context, text: explanation, isLoading: false, error: false });
                  },
                  () => {
                    if (isStale()) return;
                    setExplanation((e) => ({ ...(e || { selection }), isLoading: false, error: true }));
                  },
                );
              }}
            />
          )}
        </z-orig>
      </z-tag>
      {explanation && (
        <ExplanationModal
          open={true}
          onClose={() => {
            explainRequestRef.current++; // abandon whatever is still in flight
            setExplanation(null);
          }}
          selection={explanation.selection}
          context={explanation.context}
          explanation={explanation.text}
          isLoading={explanation.isLoading}
          error={explanation.error}
        />
      )}
      {bookmarkBeingEdited && (
        <EditBookmarkModal
          open={true}
          onClose={() => setBookmarkBeingEdited(null)}
          bookmark={bookmarkBeingEdited}
          updateBookmark={saveEditedTranslation}
          headline="Edit Translation"
          isWordEditable={false}
          showExampleField={false}
          showRemoveFromExercises={false}
        />
      )}
    </>
  );
}
